import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

import { Sparkles, RefreshCw, CreditCard, ArrowRight, Check, Copy, X, ShieldCheck } from 'lucide-react'
import { useAuth, ExtendedUser } from '../../contexts/SupabaseAuthContext'
import { supabase } from '../../config/supabase'
import OverTokenSingle from '../../assets/images/OverTokenSingle.png'
import OverTokenStack from '../../assets/images/OverTokenStack.png'
import OverTokenBag from '../../assets/images/OverTokenBag.png'


// Base Pricing: 10 tokens = 1 Real (0.10 R$/token)
const BASE_RATE = 0.10

const calculatePrice = (tokens: number) => {
  let discount = 0
  if (tokens >= 5000) discount = 0.20 // 20%
  else if (tokens >= 2000) discount = 0.15 // 15%
  else if (tokens >= 1000) discount = 0.10 // 10%

  const basePrice = tokens * BASE_RATE
  const finalPrice = basePrice * (1 - discount)

  return {
    basePrice,
    finalPrice,
    discount,
    saved: basePrice - finalPrice
  }
}

const PRODUCTS = [
  {
    id: 'A1',
    tokens: 50,
    bg: 'bg-blue-100 border-blue-300',
    size: 'scale-90',
    image: OverTokenSingle,
    label: 'MICRO'
  },
  {
    id: 'A2',
    tokens: 100,
    bg: 'bg-cyan-100 border-cyan-300',
    size: 'scale-100',
    image: OverTokenSingle,
    label: 'INICIANTE'
  },
  {
    id: 'B1',
    tokens: 500,
    bg: 'bg-purple-100 border-purple-300',
    size: 'scale-90',
    image: OverTokenStack,
    label: 'PRO PACK'
  },
  {
    id: 'B2',
    tokens: 1000,
    bg: 'bg-indigo-100 border-indigo-300',
    size: 'scale-100',
    image: OverTokenStack,
    label: 'EXPERT'
  },
  {
    id: 'C1',
    tokens: 2000,
    bg: 'bg-yellow-100 border-yellow-300',
    size: 'scale-90',
    image: OverTokenBag,
    label: 'BUSINESS'
  },
  {
    id: 'C2',
    tokens: 5000,
    bg: 'bg-emerald-100 border-emerald-300',
    size: 'scale-100',
    image: OverTokenBag,
    label: 'WHALE'
  }
]

// Manga visual components
const HalftonePattern = () => (
  <div className="absolute inset-0 opacity-10 pointer-events-none"
    style={{ backgroundImage: 'radial-gradient(circle, #000 1px, transparent 1px)', backgroundSize: '10px 10px' }}>
  </div>
)

const SpeedLines = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-20">
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[200%] h-[200%] bg-[conic-gradient(from_0deg,transparent_0deg,black_2deg,transparent_4deg)] animate-spin-slow"></div>
  </div>
)

export default function TokenVendingMachine({ navigate, initialDialogue, onOpenLogin }: { navigate: (path: string) => void, initialDialogue?: string, onOpenLogin?: () => void }) {
  const { user, loading: authLoading } = useAuth()
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)

  // Pix Data State
  const [pixData, setPixData] = useState<{ qrCode: string, copyPaste: string, intentId: string, value: number } | null>(null)
  const [copySuccess, setCopySuccess] = useState(false)
  const [timeLeft, setTimeLeft] = useState(300) // 5 minutes in seconds

  // Timer Effect
  useEffect(() => {
    if (!pixData) {
      setTimeLeft(300)
      return
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          // Sequence updates to ensure proper UI transition
          setPixData(null) // Close Modal
          setIsSucking(false) // Restore content visibility

          setTimeout(() => {
            setExpression('shocked')
            setDialogue("O tempo acabou! Tente novamente se ainda quiser os tokens.")
            // Reset to home screen logic
            setSelectedProduct(null)
            setIsCustomMode(false)
            setCustomTokens(100)
          }, 100)

          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [pixData])

  // Personality Reaction to Timer
  useEffect(() => {
    if (!pixData || timeLeft <= 0) return

    if (timeLeft === 240) { // 4 min
      setExpression('neutral')
      setDialogue("O QR Code dura 5 minutos, mas você consegue pagar em 1, né?")
    } else if (timeLeft === 120) { // 2 min
      setExpression('thinking')
      setDialogue("Tic-tac... O tempo está passando!")
    } else if (timeLeft === 60) { // 1 min
      setExpression('shocked')
      setDialogue("Último minuto! Corre antes que expire!")
    } else if (timeLeft === 30) { // 30 sec
      setExpression('shocked')
      setDialogue("30 SEGUNDOS! VAI VAI VAI!")
    }
  }, [timeLeft, pixData])

  // Custom Amount Logic
  const [isCustomMode, setIsCustomMode] = useState(false)
  const [customTokens, setCustomTokens] = useState(100)

  // Payment Method
  const [paymentMethod, setPaymentMethod] = useState<'pix' | 'card'>('pix')

  // Machine Personality State
  const [expression, setExpression] = useState<'neutral' | 'happy' | 'thinking' | 'shocked' | 'money'>('neutral')
  const [dialogue, setDialogue] = useState(initialDialogue || "Olá! Eu sou a OverMachine! O que vamos comprar hoje?")
  const [isSucking, setIsSucking] = useState(false)

  // Blink effect for eyes
  useEffect(() => {
    const blinkInterval = setInterval(() => {
      if (expression === 'neutral') {
        setExpression('thinking') // closed eyes momentarily
        setTimeout(() => setExpression('neutral'), 200)
      }
    }, 4000)
    return () => clearInterval(blinkInterval)
  }, [expression])

  const handleSelect = (id: string) => {
    setSelectedProduct(id)
    setIsCustomMode(false)
    setExpression('happy')
    setDialogue("Ótima escolha! Esse pacote é muito popular!")
  }

  const handleCustomMode = () => {
    setIsCustomMode(!isCustomMode)
    setSelectedProduct(null)
    if (!isCustomMode) {
      setExpression('shocked')
      setDialogue("Modo de Negociação?! Digite quanto você precisa!")
    } else {
      setExpression('neutral')
      setDialogue("Voltando para o menu padrão.")
    }
  }

  const handlePurchase = async () => {
    if ((!selectedProduct && !isCustomMode) || isProcessing) return

    // 1. Verificação robusta de autenticação
    let currentUser = user
    if (!currentUser) {
      console.log("Context user missing. Checking supabase.auth.getUser()...")
      setDialogue("Verificando credenciais...")

      const { data: { user: activeUser }, error: userError } = await supabase.auth.getUser()

      if (userError || !activeUser) {
        console.error("Auth check failed:", userError)
        
        if (onOpenLogin) {
          setExpression('thinking')
          setDialogue("Você precisa fazer login para comprar! Abrindo login...")
          onOpenLogin()
          return
        }

        setExpression('shocked')
        setDialogue("Você precisa fazer login! Redirecionando...")

        setTimeout(() => {
          // Tenta usar o navigate do React Router
          if (navigate) {
            navigate('/auth/login')
          }
          // Fallback agressivo para garantir o redirecionamento
          setTimeout(() => {
            window.location.href = '/auth/login'
          }, 500)
        }, 1500)
        return
      }

      console.log("User recovered via active check:", activeUser.id)
      // Cast forçado pois sabemos que o backend retorna um User válido, e nossa interface ExtendedUser é compatível em runtime
      currentUser = activeUser as unknown as ExtendedUser
    }

    setIsProcessing(true)
    setIsSucking(true) // Start sucking animation
    setExpression('thinking')
    setDialogue("Processando pedido... Sugando itens para o cofre!")

    // Play suck sound (optional)
    // const suckAudio = new Audio('/assets/SOUND/swosh.mp3'); suckAudio.play().catch(() => {});

    try {
      const amount = isCustomMode ? customTokens : PRODUCTS.find(p => p.id === selectedProduct)?.tokens
      const { finalPrice } = calculatePrice(amount || 0)

      console.log("Initiating purchase:", { amount, finalPrice, paymentMethod })

      if (paymentMethod === 'pix') {
        const { data, error } = await supabase.functions.invoke('asaas-create-pix-payment', {
          body: {
            amount: finalPrice,
            tokens: amount,
            description: `Compra de ${amount} Tokens (Overpixel)`
          }
        })

        if (error) {
          console.error("Edge Function Error:", error)
          throw error
        }

        console.log("Pix response:", data)

        if (data && data.success) {
          // Wait a bit for animation to finish
          await new Promise(resolve => setTimeout(resolve, 800))

          setPixData({ ...data, value: finalPrice })
          setExpression('happy')
          setDialogue("QR Code gerado! O pagamento já está disponível!")
          setIsSucking(false) // Stop sucking animation to show content
        } else {
          throw new Error(data?.error || 'Falha desconhecida ao gerar Pix')
        }
      } else {
        // Placeholder for Card
        setDialogue("Pagamento com cartão em breve! Use Pix por enquanto.")
        setTimeout(() => setExpression('neutral'), 3000)
        setIsSucking(false)
      }

    } catch (err: any) {
      console.error("Purchase failed:", err)
      setExpression('shocked')
      
      let errorMessage = err.message || 'Falha na comunicação';

      // Tentar extrair mensagem detalhada de erros HTTP do Supabase
      if (err && typeof err === 'object') {
         // Tentar ler corpo da resposta se disponível (algumas versões do client expõem isso)
         if (err.context && typeof err.context.json === 'function') {
            try {
               const errorBody = await err.context.json();
               if (errorBody.error) errorMessage = errorBody.error;
               if (errorBody.details) console.warn("Error Details:", errorBody.details);
            } catch (e) { /* ignore json parse error */ }
         }
      }

      // Mensagem mais amigável para erros de rede comuns
      if (errorMessage === 'Failed to fetch' || errorMessage.includes('TypeError')) {
        setDialogue("Erro de conexão! Verifique sua internet ou tente novamente.")
      } else if (errorMessage.includes('ASAAS_API_KEY')) {
        setDialogue("Erro de Configuração: Chave de API de pagamentos não encontrada.")
      } else {
        setDialogue(`Erro: ${errorMessage}`)
      }
      
      setIsSucking(false)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleCopy = async () => {
    if (!pixData?.copyPaste) return
    try {
      await navigator.clipboard.writeText(pixData.copyPaste)
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000)
    } catch (err) {
      console.error('Failed to copy!', err)
    }
  }

  // Poll for payment status
  useEffect(() => {
    if (!pixData?.intentId) return

    const interval = setInterval(async () => {
      const { data } = await supabase
        .from('purchase_intents')
        .select('status')
        .eq('id', pixData.intentId)
        .single()

      if (data?.status === 'completed') {
        clearInterval(interval)

        // SUCCESS SEQUENCE
        setPixData(null)
        setShowSuccess(true)
        setExpression('money')
        setDialogue("PAGAMENTO CONFIRMADO! RECARGA REALIZADA COM SUCESSO!")

        // Play Cash Sound
        try {
          const audio = new Audio('/assets/SOUND/pop-sound-423716.mp3')
          audio.volume = 0.8
          audio.play().catch(e => console.log("Audio play failed (user interaction needed or file missing)", e))
        } catch (e) { }

        // Reset after 5 seconds
        setTimeout(() => {
          setShowSuccess(false)
          setExpression('neutral')
          setIsSucking(false)
          setDialogue("Pronto para a próxima compra! O que vai ser?")

          // Reset to home screen
          setSelectedProduct(null)
          setIsCustomMode(false)
          setCustomTokens(100)
        }, 5000)
      }
    }, 3000)

    return () => clearInterval(interval)
  }, [pixData])

  const currentTokens = isCustomMode
    ? customTokens
    : selectedProduct
      ? PRODUCTS.find(p => p.id === selectedProduct)?.tokens || 0
      : 0

  const { finalPrice, discount, saved } = calculatePrice(currentTokens)

  return (
    <div className="relative w-full h-full font-sans">

      {/* Main Machine Container - MANGA STYLE */}
      <div className="relative w-full h-full bg-white flex flex-col md:flex-row overflow-hidden">
        <HalftonePattern />

        {/* --- LEFT SIDE: THE MACHINE FACE (Personality) --- */}
        {/* Mobile: Top Bar | Desktop: Sidebar */}
        <div className="w-full md:w-[320px] bg-sky-100 border-b-2 md:border-b-0 md:border-r-4 border-black p-3 md:p-6 flex flex-row md:flex-col items-center md:items-stretch gap-3 md:gap-0 relative overflow-hidden">
          <SpeedLines />

          {/* Machine Brand - Hidden on Mobile to save space */}
          <div className="hidden md:inline-block bg-black text-white px-4 py-2 transform -skew-x-12 self-start mb-4 md:mb-6 border-2 border-white shadow-[4px_4px_0px_0px_rgba(0,0,0,0.2)] z-20 relative">
            <span className="font-black italic text-lg md:text-xl tracking-wider">OVER-VEND</span>
          </div>



          {/* The Eyes (Character) */}
          <div className="w-14 h-14 md:w-full md:h-auto md:aspect-video bg-black rounded-xl border-2 md:border-4 border-gray-800 p-1 md:p-4 flex items-center justify-center gap-1 md:gap-6 shadow-inner relative z-10 shrink-0">
            {expression === 'money' ? (
              <div className="flex gap-1 md:gap-4">
                <motion.div
                  animate={{ scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] }}
                  transition={{ repeat: Infinity, duration: 0.5 }}
                  className="text-lg md:text-6xl font-black text-green-400 drop-shadow-[0_0_10px_rgba(74,222,128,0.8)]"
                >
                  $
                </motion.div>
                <motion.div
                  animate={{ scale: [1, 1.2, 1], rotate: [0, -10, 10, 0] }}
                  transition={{ repeat: Infinity, duration: 0.5, delay: 0.1 }}
                  className="text-lg md:text-6xl font-black text-green-400 drop-shadow-[0_0_10px_rgba(74,222,128,0.8)]"
                >
                  $
                </motion.div>
              </div>
            ) : (
              <>
                {/* Left Eye */}
                <motion.div
                  animate={{
                    height: expression === 'thinking' ? 2 : (window.innerWidth < 768 ? 24 : 64),
                    scaleY: expression === 'happy' ? 1.2 : 1
                  }}
                  className={`w-3 md:w-16 bg-cyan-400 rounded-full border md:border-4 border-cyan-200 shadow-[0_0_20px_rgba(34,211,238,0.8)] relative overflow-hidden`}
                >
                  {expression === 'happy' && <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-black/20 rounded-t-full"></div>}
                </motion.div>

                {/* Right Eye */}
                <motion.div
                  animate={{
                    height: expression === 'thinking' ? 2 : (window.innerWidth < 768 ? 24 : 64),
                    scaleY: expression === 'happy' ? 1.2 : 1
                  }}
                  className={`w-3 md:w-16 bg-cyan-400 rounded-full border md:border-4 border-cyan-200 shadow-[0_0_20px_rgba(34,211,238,0.8)] relative overflow-hidden`}
                >
                  {expression === 'happy' && <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-black/20 rounded-t-full"></div>}
                </motion.div>
              </>
            )}
          </div>

          {/* Speech Bubble */}
          <div className="relative flex-1 md:flex-none bg-white border-2 md:border-4 border-black p-2 md:p-6 rounded-xl md:rounded-2xl shadow-[2px_2px_0px_0px_rgba(0,0,0,0.1)] md:shadow-[8px_8px_0px_0px_rgba(0,0,0,0.1)] md:mb-auto z-10 min-h-[40px] md:min-h-[50px] flex items-center">
            {/* Triangle Pointer - Desktop Only */}
            <div className="hidden md:block absolute -top-4 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[15px] border-l-transparent border-r-[15px] border-r-transparent border-b-[20px] border-b-black"></div>
            <div className="hidden md:block absolute -top-[13px] left-1/2 -translate-x-1/2 w-0 h-0 border-l-[12px] border-l-transparent border-r-[12px] border-r-transparent border-b-[17px] border-b-white"></div>

            {/* Triangle Pointer - Mobile (Left pointing) */}
            <div className="md:hidden absolute top-1/2 -translate-y-1/2 -left-2 w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-r-[8px] border-r-black"></div>
            <div className="md:hidden absolute top-1/2 -translate-y-1/2 -left-[6px] w-0 h-0 border-t-[4px] border-t-transparent border-b-[4px] border-b-transparent border-r-[6px] border-r-white"></div>

            <p className="font-bold text-[10px] md:text-lg leading-tight text-gray-800 uppercase italic line-clamp-3 md:line-clamp-none">
              "{dialogue}"
            </p>
          </div>

          {/* Status Indicators - Desktop Only */}
          <div className="hidden md:block mt-6 space-y-3 z-10">
            <div className="flex items-center gap-2 font-bold border-2 border-black bg-white p-2 rounded-lg text-black">
              <div className={`w-4 h-4 rounded-full border-2 border-black ${isProcessing ? 'bg-yellow-400 animate-pulse' : 'bg-green-400'}`}></div>
              <span className="text-sm">SISTEMA: ONLINE</span>
            </div>
            <div className="flex items-center gap-2 font-bold border-2 border-black bg-white p-2 rounded-lg text-black">
              <div className="w-4 h-4 rounded-full border-2 border-black bg-blue-400 animate-pulse"></div>
              <span className="text-sm">CONEXÃO: ESTÁVEL</span>
            </div>
          </div>

        </div>

        {/* --- RIGHT SIDE: INTERFACE --- */}
        <div className="flex-1 bg-white p-3 md:p-8 pb-6 md:pb-10 flex flex-col relative">

          {/* Header Actions */}
          <div className="flex justify-between items-center mb-2 md:mb-6">
            <h2 className="text-base md:text-3xl font-black italic uppercase tracking-tighter transform -skew-x-6 decoration-4 decoration-yellow-400 underline underline-offset-4 text-black">
              {isCustomMode ? 'MODO LIVRE' : 'LOJA'}
            </h2>
            <button
              onClick={handleCustomMode}
              className="group flex items-center gap-1 md:gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-black text-white font-bold text-[10px] md:text-base rounded-lg hover:bg-yellow-400 hover:text-black transition-colors border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,0.3)] md:shadow-[4px_4px_0px_0px_rgba(0,0,0,0.3)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px]"
            >
              <RefreshCw className="w-3 h-3 md:w-4 md:h-4 group-hover:rotate-180 transition-transform" />
              {isCustomMode ? 'VOLTAR' : 'PERSONALIZAR'}
            </button>
          </div>

          {/* CONTENT AREA SWAPPER */}
          <div className="flex-1 relative min-h-0 overflow-y-auto pr-2 custom-scrollbar">
            <motion.div
              animate={isSucking ? { scale: 0, opacity: 0, rotate: 720 } : { scale: 1, opacity: 1, rotate: 0 }}
              transition={{ duration: 0.8, ease: "easeInOut" }}
              className="h-full"
            >
              <AnimatePresence mode="wait">
                {pixData ? (
                  // --- PIX QR CODE MODE (INLINE) ---
                  <motion.div
                    key="pix"
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    className="flex flex-col h-full bg-white rounded-xl border-4 border-black p-4 md:p-6 relative shadow-[0_0_20px_rgba(6,182,212,0.3)]"
                  >
                    <HalftonePattern />
                    
                    {/* Header */}
                    <div className="text-center mb-2 z-10">
                      <h3 className="text-xl md:text-2xl font-black italic uppercase mb-1">PAGUE COM PIX</h3>
                      <p className="text-xs text-gray-500 font-bold">Escaneie ou copie o código abaixo</p>
                    </div>

                    {/* QR Code Area */}
                    <div className="flex-1 flex flex-col items-center justify-center z-10 min-h-0">
                        <div className="bg-gray-100 p-3 rounded-xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center mb-2 group cursor-pointer w-full max-w-[200px]" onClick={handleCopy}>
                          <div className="bg-white p-2 rounded-lg shadow-sm mb-2 relative w-full aspect-square">
                            <img src={`data:image/png;base64,${pixData.qrCode}`} alt="Pix QR Code" className="w-full h-full object-contain mix-blend-multiply" />
                            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-400/20 to-transparent h-2 w-full animate-scan pointer-events-none"></div>
                          </div>
                          <p className="text-[10px] font-mono text-gray-400 group-hover:text-cyan-600 transition-colors">Clique para copiar</p>
                        </div>
                    </div>

                    {/* Footer Actions */}
                    <div className="z-10 mt-auto space-y-2">
                         <button
                          onClick={handleCopy}
                          className={`w-full py-2 rounded-lg border-2 border-black font-bold flex items-center justify-center gap-2 transition-all ${copySuccess ? 'bg-green-100 text-green-700 border-green-500' : 'bg-gray-50 hover:bg-gray-100 text-black'}`}
                        >
                          {copySuccess ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          {copySuccess ? 'COPIADO!' : 'COPIAR CÓDIGO'}
                        </button>

                        <div className="flex items-center justify-between text-[10px] md:text-xs font-bold text-gray-400">
                          <span className="flex items-center gap-1">
                            <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                            Aguardando...
                          </span>
                          <span className={`font-mono ${timeLeft < 60 ? 'text-red-500 animate-pulse' : ''}`}>
                            Expira: {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
                          </span>
                        </div>
                    </div>

                    {/* Close Button */}
                    <button
                      onClick={() => setPixData(null)}
                      className="absolute top-2 right-2 p-1.5 hover:bg-gray-100 rounded-full transition-colors z-20"
                    >
                      <X className="w-5 h-5 text-gray-400" />
                    </button>
                  </motion.div>
                ) : !isCustomMode ? (
                  // --- SHELF MODE ---
                  <motion.div
                    key="shelf"
                    initial={{ x: -50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: 50, opacity: 0 }}
                    className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3 pb-4"
                  >
                    {PRODUCTS.map((product) => {
                      const { finalPrice: price, discount: prodDiscount } = calculatePrice(product.tokens)
                      return (
                        <motion.button
                          key={product.id}
                          onClick={() => handleSelect(product.id)}
                          whileHover={{ scale: 1.02, rotate: -1 }}
                          whileTap={{ scale: 0.98 }}
                          className={`relative border-2 md:border-4 border-black rounded-lg md:rounded-xl p-2 md:p-3 flex flex-col items-center gap-1 md:gap-2 transition-colors ${selectedProduct === product.id ? 'bg-yellow-200' : 'bg-gray-50 hover:bg-white'}`}
                        >
                          {/* Shadow Block */}
                          <div className="absolute inset-0 bg-black rounded-lg md:rounded-xl translate-x-1 translate-y-1 md:translate-x-2 md:translate-y-2 -z-10"></div>

                          {/* Discount Sticker */}
                          {prodDiscount > 0 && (
                            <div className="absolute -top-2 -right-2 md:-top-2 md:-right-2 bg-red-500 text-white font-black text-[9px] md:text-[10px] px-1.5 py-0.5 md:px-2 md:py-0.5 rotate-12 border border-black shadow-sm z-20">
                              -{prodDiscount * 100}%
                            </div>
                          )}

                          <div className={`w-24 h-24 md:w-32 md:h-32 rounded-full ${product.bg} border-2 md:border-4 border-black flex items-center justify-center shadow-inner relative overflow-hidden group-hover:scale-110 transition-transform duration-300`}>
                            {/* Background Glow */}
                            <div className="absolute inset-0 bg-white/40 rounded-full blur-xl transform -translate-y-1/2"></div>

                            {/* Product Image - Scaled and Blended */}
                            <img
                              src={product.image}
                              alt={`${product.label} Icon`}
                              className={`w-full h-full object-cover mix-blend-multiply filter contrast-125 hover:brightness-110 transition-all ${product.size}`}
                            />
                          </div>

                          <div className="text-center">
                            <span className="block font-black text-lg md:text-2xl text-gray-900 leading-none">{product.tokens}</span>
                            <span className="block text-[9px] md:text-[10px] font-bold uppercase tracking-wider text-gray-500 bg-gray-200 px-1 rounded mt-0.5">TOKENS</span>
                          </div>

                          <div className="w-full border-t border-dashed border-gray-300 my-0 md:my-0.5"></div>

                          <div className="text-center">
                            {prodDiscount > 0 && (
                              <span className="block text-[9px] md:text-[10px] text-gray-400 line-through decoration-red-500">R$ {calculatePrice(product.tokens).basePrice.toFixed(2)}</span>
                            )}
                            <span className="block font-black text-sm md:text-lg text-green-600">R$ {price.toFixed(2)}</span>
                          </div>
                        </motion.button>
                      )
                    })}
                  </motion.div>
                ) : (
                  // --- CUSTOM MODE ---
                  <motion.div
                    key="custom"
                    initial={{ x: 50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: -50, opacity: 0 }}
                    className="flex flex-col h-full bg-gray-50 rounded-xl border-4 border-black p-4 md:p-8 relative"
                  >
                    <HalftonePattern />
                    <div className="text-center mb-6 z-10">
                      <h3 className="text-xl md:text-2xl font-black uppercase italic mb-2">QUANTO VOCÊ PRECISA?</h3>
                      <p className="text-sm text-gray-600 font-medium">Digite a quantidade exata de tokens.</p>
                    </div>

                    <div className="flex-1 flex flex-col items-center justify-center gap-6 z-10">
                      <div className="relative w-full max-w-xs">
                        <input
                          type="number"
                          value={customTokens}
                          onChange={(e) => setCustomTokens(Math.max(10, parseInt(e.target.value) || 0))}
                          className="w-full text-center text-4xl md:text-6xl font-black bg-white border-4 border-black rounded-xl py-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,0.1)] focus:outline-none focus:shadow-[8px_8px_0px_0px_rgba(6,182,212,1)] transition-all"
                        />
                        <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-black text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                          Tokens
                        </span>
                      </div>

                      <div className="bg-white border-2 border-black p-4 rounded-xl w-full max-w-xs shadow-sm">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-bold text-gray-500 uppercase">Preço Base</span>
                          <span className="font-mono font-bold">R$ {(customTokens * BASE_RATE).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center mb-2 text-green-600">
                          <span className="text-sm font-bold uppercase">Desconto ({discount * 100}%)</span>
                          <span className="font-mono font-bold">- R$ {saved.toFixed(2)}</span>
                        </div>
                        <div className="border-t-2 border-dashed border-gray-200 my-2"></div>
                        <div className="flex justify-between items-center text-xl">
                          <span className="font-black italic uppercase">TOTAL</span>
                          <span className="font-black text-green-600">R$ {finalPrice.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>

          {/* CHECKOUT AREA */}
          <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t-4 border-black relative z-20">
            <div className="flex flex-col gap-3 md:gap-4">
              {/* Payment Method Selector */}
              <div className="flex bg-gray-100 p-1 rounded-lg border-2 border-gray-300">
                <button
                  onClick={() => setPaymentMethod('pix')}
                  className={`flex-1 py-1 md:py-2 rounded-md font-bold text-xs md:text-sm flex items-center justify-center gap-2 transition-all ${paymentMethod === 'pix' ? 'bg-white text-black shadow-sm border border-gray-200' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <Sparkles className="w-4 h-4 text-yellow-500" /> PIX (INSTANTÂNEO)
                </button>
                <button
                  onClick={() => setPaymentMethod('card')}
                  className={`flex-1 py-1 md:py-2 rounded-md font-bold text-xs md:text-sm flex items-center justify-center gap-2 transition-all ${paymentMethod === 'card' ? 'bg-white text-black shadow-sm border border-gray-200' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <CreditCard className="w-4 h-4" /> CARTÃO (EM BREVE)
                </button>
              </div>

              {/* BUY BUTTON */}
              <button
                onClick={handlePurchase}
                disabled={isProcessing || (!selectedProduct && !isCustomMode)}
                className="w-full relative group"
              >
                <div className="absolute inset-0 bg-black rounded-xl translate-y-2 translate-x-0 group-hover:translate-y-3 transition-transform"></div>
                <div className={`relative w-full py-3 md:py-5 rounded-xl border-4 border-black flex items-center justify-center gap-3 font-black text-lg md:text-2xl uppercase tracking-widest transition-transform group-active:translate-y-2 ${(!selectedProduct && !isCustomMode) ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-green-400 hover:bg-green-300 text-black cursor-pointer'}`}>
                  {isProcessing ? (
                    <>
                      <RefreshCw className="w-6 h-6 animate-spin" /> PROCESSANDO...
                    </>
                  ) : (
                    <>
                      COMPRAR AGORA <ArrowRight className="w-6 h-6" />
                    </>
                  )}
                </div>
              </button>
            </div>
          </div>

        </div>



        {/* --- SUCCESS OVERLAY --- */}
        <AnimatePresence>
          {showSuccess && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 z-50 bg-green-500 flex flex-col items-center justify-center text-center p-8"
            >
              <motion.div
                initial={{ scale: 0.5, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", bounce: 0.5 }}
              >
                <ShieldCheck className="w-32 h-32 text-white mb-6 drop-shadow-lg" />
              </motion.div>
              <motion.h2
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-4xl md:text-6xl font-black text-white italic tracking-tighter mb-4 drop-shadow-md"
              >
                SUCESSO!
              </motion.h2>
              <motion.p
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="text-xl text-green-100 font-bold max-w-md"
              >
                Seus tokens foram adicionados à sua conta. Aproveite!
              </motion.p>
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </div>
  )
}
